import type { Customer360Snapshot } from "../../../types/crm";
import clsx from "clsx";

interface Props {
  marketing: Customer360Snapshot["marketing"];
  onRefresh?: () => void;
  refreshing?: boolean;
}

function formatCurrency(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPercent(n: number | null | undefined, digits = 1): string {
  if (n == null) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatCount(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString();
}

/** Hero tile — the big glance KPIs admin sees first */
function HeroTile({
  label,
  value,
  sub,
  trend,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  trend?: number | null;
  accent?: "neutral" | "green" | "red" | "amber";
}) {
  const valueCls =
    accent === "green"
      ? "text-emerald-700"
      : accent === "red"
        ? "text-rose-700"
        : accent === "amber"
          ? "text-amber-700"
          : "text-content-primary";
  let trendStr = "";
  let trendCls = "text-content-tertiary";
  if (trend != null) {
    trendStr = `${trend > 0 ? "↑" : trend < 0 ? "↓" : "→"} ${Math.abs(trend * 100).toFixed(1)}%`;
    trendCls =
      trend > 0
        ? "text-emerald-600"
        : trend < 0
          ? "text-rose-600"
          : "text-content-tertiary";
  }
  return (
    <div className="p-4 flex flex-col justify-between min-h-[92px]">
      <div className="text-[10px] uppercase tracking-wider font-medium text-content-tertiary">
        {label}
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-1">
        <span
          className={clsx(
            "text-2xl font-semibold tabular-nums leading-tight",
            valueCls,
          )}
        >
          {value}
        </span>
        {trendStr && (
          <span className={clsx("text-xs font-medium", trendCls)}>
            {trendStr}
          </span>
        )}
      </div>
      {sub && (
        <div className="text-[11px] text-content-tertiary mt-0.5">{sub}</div>
      )}
    </div>
  );
}

/** Secondary tile — small, dense */
function MiniTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="p-3 flex flex-col justify-center min-h-[68px]">
      <div className="text-[10px] uppercase tracking-wider text-content-tertiary font-medium">
        {label}
      </div>
      <div className="text-base font-semibold tabular-nums text-content-primary mt-0.5 leading-tight">
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-content-tertiary mt-0.5">{sub}</div>
      )}
    </div>
  );
}

/**
 * 360-page marketing block. Layout: 4 hero KPIs (Bookings, Bookings $,
 * Spend, Margin) on top so the admin reads revenue-facing numbers in one
 * glance; 7 dense supporting tiles below for funnel inputs + derived
 * rates. Commission rate + shares live in the smaller row because they're
 * rarely the story.
 */
export function MarketingSection({ marketing, onRefresh, refreshing }: Props) {
  const header = (
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-secondary">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-content-primary">
          Marketing
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
          last 30 days
        </span>
      </div>
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
        >
          {refreshing ? "Refreshing…" : "Refresh live"}
        </button>
      )}
    </div>
  );

  if (!marketing) {
    return (
      <section className="bg-white rounded-xl border border-border-secondary shadow-sm">
        {header}
        <div className="px-5 py-8 text-sm text-content-tertiary italic">
          No marketing snapshot yet — will be generated on the next cron run.
        </div>
      </section>
    );
  }

  // Margin computation — same formula the dashboard column uses.
  const rate = marketing.commissionRate;
  const bookings = marketing.bookingsValueUsd30d;
  const spend = marketing.spend30d;
  const hasMargin =
    rate != null && rate > 0 && bookings != null && spend != null;
  const marginUsd = hasMargin ? (bookings as number) * (rate as number) - (spend as number) : null;

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm overflow-hidden">
      {header}

      {/* Hero row — 4 big tiles, revenue-first */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border-secondary border-b border-border-secondary">
        <HeroTile
          label="Bookings"
          value={formatCount(marketing.bookings30d)}
          trend={marketing.bookingsTrendDelta}
        />
        <HeroTile
          label="Bookings Value"
          value={formatCurrency(marketing.bookingsValueUsd30d)}
        />
        <HeroTile
          label="Ad Spend"
          value={formatCurrency(marketing.spend30d)}
        />
        <HeroTile
          label="Margin"
          value={
            hasMargin
              ? `${marginUsd! < 0 ? "-" : ""}${new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                }).format(Math.abs(marginUsd!))}`
              : "—"
          }
          accent={
            hasMargin
              ? marginUsd! > 0
                ? "green"
                : marginUsd! < 0
                  ? "red"
                  : "neutral"
              : "neutral"
          }
          sub={hasMargin ? "Commission − Ad Spend" : "No commission plan"}
        />
      </div>

      {/* Secondary row — 7 compact tiles, funnel + rates */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 divide-x divide-y lg:divide-y-0 divide-border-secondary">
        <MiniTile
          label="Impressions"
          value={formatCount(marketing.impressions30d)}
        />
        <MiniTile label="Clicks" value={formatCount(marketing.clicks30d)} />
        <MiniTile
          label="Traffic Share"
          value={formatPercent(marketing.autumnShareOfTraffic)}
        />
        <MiniTile
          label="Booking Share"
          value={formatPercent(marketing.autumnShareOfBookings)}
        />
        <MiniTile
          label="Commission"
          value={formatPercent(marketing.commissionRate, 2)}
        />
        <MiniTile
          label="Occupancy Lift"
          value={formatPercent(marketing.occupancyLift30d)}
          sub="vs prior 30d"
        />
        <MiniTile
          label="RevPAR Δ"
          value={formatPercent(marketing.revParImprovement30d)}
          sub={
            marketing.adrChange30d != null
              ? `ADR ${formatPercent(marketing.adrChange30d)}`
              : undefined
          }
        />
      </div>
    </section>
  );
}
